import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PasswordInput from './ui/PasswordInput';
import PasswordError from './ui/PasswordError';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import Header from './Header';
import { usePlans, errorToMessage } from '../lib/plansClient';
import { usePlanPricing } from '../lib/usePlanPricing';
import { featuresToLabels, getCurrentLang } from '../lib/featureLabels';
import { planName, planDescription, planNameFromPackages } from '../lib/planMeta';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import LegalDocumentModal from './LegalDocumentModal';
import {
  TERMS_TITLE, TERMS_CONTENT, TERMS_VERSION,
  PRIVACY_TITLE, PRIVACY_CONTENT, PRIVACY_VERSION,
} from '../lib/legalContent';
import { CreditCard, Check, ShieldCheck, XCircle, Sparkles, Clock, ArrowRight, Info } from 'lucide-react';
import { validatePassword, validatePasswordMatch } from '../utils/passwordValidation';

// Backend URL — promo code doğrulama endpointi public'tir.
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    contact_email: '',
    password: '',
    confirm_password: '',
    selected_plan: 'solo', // Default: Solo (en ucuz, sol-üst)
    subscription_type: 'monthly',
    agreed_terms: false,
    base_currency: 'TRY', // Default base currency
    is_company: false,
    company_name: '',
    tax_number: '',
    address: '',
    city: '',
    country: 'TR'
  });
  
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loading, setLoading] = useState(false);
  const { registerCompany } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [activeCampaign, setActiveCampaign] = useState(null);

  // Promosyon Kodu — Ultra Admin tarafından oluşturulan davet kodu. URL'den
  // (?promo=XYZ) okunur ve register submit ile birlikte backend'e gönderilir.
  const [promoCode, setPromoCode] = useState('');
  const [promoInfo, setPromoInfo] = useState(null);     // validate response
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);
  
  // Use new plansClient hook for real-time sync
  const { plans: availablePackages, loading: loadingPackages, error: plansError } = usePlans();
  
  // Use pricing hook for dynamic prices in order summary
  const { price, symbol, planDetails } = usePlanPricing();

  // Load the currently active promotional campaign (if any). Ultra Admin
  // manages these under Özellik Yönetimi > Kampanyalar. Selection rule:
  // active AND (start<=now AND end>=now) — highest discount wins.
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/public/active-campaign`);
        if (!res.ok) return;
        const data = await res.json();
        setActiveCampaign(data.campaign || null);
      } catch (err) {
        // Silently ignore — banner is a nice-to-have, not critical.
      }
    };
    fetchCampaign();
  }, []);


  // Load countries on mount
  useEffect(() => {
    console.log('🔥 RegisterPage: Countries useEffect triggered!');
    const fetchCountries = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        console.log('📍 Fetching countries from:', `${backendUrl}/api/countries`);
        const response = await fetch(`${backendUrl}/api/countries`);
        const data = await response.json();
        console.log('✅ Countries loaded:', data.length);
        setCountries(data);
      } catch (error) {
        console.error('❌ Error fetching countries:', error);
        toast.error('Ülkeler yüklenemedi');
      }
    };
    fetchCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    console.log('🏙️ RegisterPage: Cities useEffect triggered for country:', formData.country);
    const fetchCities = async () => {
      if (!formData.country) {
        console.log('⏭️ No country selected, skipping cities fetch');
        return;
      }
      
      setLoadingCities(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const url = `${backendUrl}/api/cities?country=${formData.country}`;
        console.log('📍 Fetching cities from:', url);
        const response = await fetch(url);
        const data = await response.json();
        console.log('✅ Cities loaded:', data.length);
        setCities(data);
      } catch (error) {
        console.error('❌ Error fetching cities:', error);
        toast.error('Şehirler yüklenemedi');
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [formData.country]);

  // Packages are now loaded via usePlans hook (real-time sync with BroadcastChannel)
  useEffect(() => {
    if (plansError) {
      console.error('Plans error:', plansError);
      toast.error(`Paketler yüklenemedi: ${plansError}`);
    } else if (availablePackages.length > 0) {
      console.log('📦 Available plans:', availablePackages);
    }
  }, [availablePackages, plansError]);

  // Safety: if currently selected plan gets set to Passive by Ultra Admin (or
  // was pre-selected via URL param but no longer active), auto-shift the user
  // to the first available active plan so the form always has a valid choice.
  useEffect(() => {
    if (!availablePackages || availablePackages.length === 0) return;
    const activeCodes = new Set(availablePackages.map(p => (p.code || p.id || '').toLowerCase()));
    if (!activeCodes.has(String(formData.selected_plan || '').toLowerCase())) {
      // Preferred order: solo → starter → professional → enterprise → first available
      const preferred = ['solo', 'starter', 'professional', 'enterprise'];
      const fallback = preferred.find(c => activeCodes.has(c)) || [...activeCodes][0];
      if (fallback) {
        setFormData(prev => ({ ...prev, selected_plan: fallback }));
      }
    }
  }, [availablePackages, formData.selected_plan]);

  useEffect(() => {
    const checkInviteStatus = async (inviteToken) => {
      try {
        // Decode base64 token to get invite_id
        const decoded = atob(inviteToken);
        const inviteInfo = JSON.parse(decoded);
        
        console.log('Checking invite status for:', inviteInfo.invite_id);
        
        // Check invite status with backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/auth/check-invite/${inviteInfo.invite_id}`);
        const data = await response.json();
        
        console.log('Invite status response:', data);
        
        if (!data.valid) {
          // Invite is not valid (cancelled, expired, used, or not found)
          if (data.status === 'cancelled') {
            // Redirect to cancelled invite page
            console.log('Redirecting to cancelled page');
            navigate(`/invite-cancelled?email=${encodeURIComponent(data.email || inviteInfo.email)}`);
            return;
          } else if (data.status === 'expired') {
            toast.error('Bu davet süresi dolmuş. Lütfen yöneticinizle iletişime geçin.');
            return;
          } else if (data.status === 'used') {
            toast.error('Bu davet zaten kullanılmış.');
            return;
          } else {
            toast.error('Geçersiz davet linki');
            return;
          }
        }
        
        // Invite is valid, proceed with pre-filling form
        setInviteData(inviteInfo);
        
        setFormData(prev => ({
          ...prev,
          contact_email: inviteInfo.email,
          company_name: inviteInfo.company_name || '',
          selected_plan: 'solo',
          subscription_type: 'monthly'
        }));
        
        toast.success(`🎉 Özel davet! ${inviteInfo.trial_days} gün ücretsiz Enterprise deneme süresi!`);
        
      } catch (error) {
        console.error('Invalid invite token:', error);
        toast.error('Geçersiz davet linki');
      }
    };

    // Check if a plan is selected from landing page
    const planFromUrl = searchParams.get('plan');
    if (planFromUrl && ['solo', 'starter', 'professional', 'enterprise'].includes(planFromUrl)) {
      setFormData(prev => ({
        ...prev,
        selected_plan: planFromUrl
      }));
    }

    // Check if there's an invite token
    const inviteToken = searchParams.get('invite');
    if (inviteToken) {
      // First, check invite status with backend
      checkInviteStatus(inviteToken);
    }

    // Promo code URL parametresi — otomatik doldur + validate.
    const promoFromUrl = searchParams.get('promo');
    if (promoFromUrl) {
      const codeUp = promoFromUrl.toUpperCase();
      setPromoCode(codeUp);
      // Otomatik doğrulama — geçerliyse e-posta prefill + plan kilitleme
      (async () => {
        setPromoChecking(true);
        try {
          const res = await axios.post(`${API}/promo-codes/validate`, { code: codeUp });
          setPromoInfo(res.data);
          // Plan'ı promo'nun planına zorla + e-postayı doldur (readonly).
          setFormData((prev) => ({
            ...prev,
            selected_plan: res.data.selected_plan || prev.selected_plan,
            contact_email: res.data.invite_email || prev.contact_email,
          }));
        } catch (err) {
          setPromoError(err.response?.data?.detail || 'Kod doğrulanamadı');
          setPromoInfo(null);
        } finally {
          setPromoChecking(false);
        }
      })();
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear password errors when user starts typing (errors shown on blur)
    if (name === 'password' && passwordError) {
      setPasswordError('');
    }
    
    if (name === 'confirm_password' && confirmPasswordError) {
      setConfirmPasswordError('');
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // onBlur event handlers for password validation
  const handlePasswordBlur = () => {
    if (formData.password) {
      setPasswordError(validatePassword(formData.password));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.confirm_password) {
      setConfirmPasswordError(validatePasswordMatch(formData.password, formData.confirm_password));
    }
  };

  const getSelectedPlanInfo = () => {
    // Dynamic values based on invite data
    const trialDays = inviteData?.trial_days || 14;
    const maxUsers = inviteData?.max_users || 15;
    
    // Free trial default (always available)
    const freeTrialPlan = {
      name: `${trialDays} Günlük Ücretsiz Deneme`,
      price: 0,
      user_limit: maxUsers,
      features: ['Tüm özellikler', `${trialDays} gün ücretsiz`, `${maxUsers} kullanıcıya kadar`, 'Kredi kartı gerekmez'],
      description: inviteData ? 'Özel davet ile tam özellikli enterprise deneme' : 'Tam özellikli deneme sürümü'
    };
    
    // If free trial selected, return it
    if (formData.selected_plan === 'free_trial') {
      return freeTrialPlan;
    }
    
    // Find plan from API data using new format (code field)
    const selectedPlan = availablePackages.find(p => p.code === formData.selected_plan || p.id === formData.selected_plan);
    
    if (selectedPlan) {
      // Convert new format to old format for compatibility
      // Öncelik: Ultra Admin > Paket Düzenle > "Ana Sayfa Özellik Maddeleri"
      // (marketing_features). Yoksa feature-ID listesi kullanılır ve aşağıdaki
      // featuresToLabels() üzerinden Türkçe etikete çevrilir.
      const marketingBullets = Array.isArray(selectedPlan.marketing_features)
        ? selectedPlan.marketing_features.filter(Boolean)
        : [];
      const bullets = marketingBullets.length > 0
        ? marketingBullets
        : (Array.isArray(selectedPlan.features)
            ? selectedPlan.features.map(f => String(f)).filter(Boolean)
            : ['Temel özellikler dahil']);
      return {
        name: String(selectedPlan.title || ''),
        price: Number(selectedPlan.price) || 0,
        user_limit: selectedPlan.userLimit || 0,
        description: String(selectedPlan.description || ''),
        features: bullets,
        // Flag so UI can skip the feature-ID → i18n label mapping when the
        // list is already human-readable marketing copy.
        featuresArePlainText: marketingBullets.length > 0,
      };
    }
    
    // Fallback to free trial
    return freeTrialPlan;
  };

  // Reactive plan info - recalculates when formData.selected_plan changes
  const selectedPlan = getSelectedPlanInfo();

  // Legal (Terms + Privacy) modals — kabul için okuma zorunlu
  const [legalModal, setLegalModal] = useState({ show: false, type: null });
  const [termsRead, setTermsRead] = useState(false);
  const [privacyRead, setPrivacyRead] = useState(false);
  const [legalAcceptedAt, setLegalAcceptedAt] = useState(null); // Son onay ISO timestamp
  const legalReady = termsRead && privacyRead;

  // UI dili + dinamik özellik etiketleri (Ultra Admin > Özellik Yönetimi kodları)
  const lang = React.useMemo(() => getCurrentLang(), []);
  const planFeatureLabels = React.useMemo(
    () => (selectedPlan.featuresArePlainText
      ? (selectedPlan.features || [])          // marketing_features → oldu gibi göster
      : featuresToLabels(selectedPlan.features || [], lang)),
    [selectedPlan.features, selectedPlan.featuresArePlainText, lang]
  );
  
  // Debug log - remove after testing
  console.log('RegisterPage - selected_plan:', formData.selected_plan, 'price:', selectedPlan.price);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setPasswordError('');
    setConfirmPasswordError('');
    setEmailError('');
    
    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }
    
    if (formData.password !== formData.confirm_password) {
      setConfirmPasswordError('Şifreler eşleşmiyor');
      return;
    }
    
    if (!formData.agreed_terms) {
      toast.error('Kullanım koşullarını kabul etmelisiniz');
      return;
    }
    
    // Company validation
    if (formData.is_company) {
      if (!formData.company_name?.trim()) {
        toast.error('Şirket adı zorunludur');
        return;
      }
      if (!formData.address?.trim()) {
        toast.error('Adres zorunludur');
        return;
      }
      if (!formData.country) {
        toast.error('Ülke seçimi zorunludur');
        return;
      }
      if (!formData.city) {
        toast.error('Şehir seçimi zorunludur');
        return;
      }
      // Vergi numarası opsiyonel - validation yok
    }

    setLoading(true);

    try {
      const registrationData = {
        contact_email: formData.contact_email,
        password: formData.password,
        subscription_type: formData.subscription_type,
        selected_plan: formData.selected_plan,
        base_currency: 'TRY',  // Default TRY, will be set in onboarding wizard
        // Add company data if is_company is checked
        company_name: formData.is_company ? formData.company_name : null,
        tax_number: formData.is_company ? formData.tax_number : null,
        address: formData.is_company ? `${formData.address}, ${formData.city}, ${countries.find(c => c.id === formData.country)?.name || formData.country}` : null,
        // Add invite data if available
        invite_data: inviteData,
        // Promosyon kodu (varsa) — backend validate edip plan + trial + indirim
        // meta'sını uygular.
        promo_code: promoCode ? promoCode.trim().toUpperCase() : null,
        // KVKK: yasal kabul kanıtı — backend'de user + company kaydına eklenir
        legal_terms_version: TERMS_VERSION,
        legal_privacy_version: PRIVACY_VERSION,
        legal_accepted_at: legalAcceptedAt || new Date().toISOString(),
      };

      const success = await registerCompany(registrationData);
      
      // Handle different response types
      if (success === true) {
        // Free trial - direct to dashboard
        toast.success('Kayıt başarılı! Dashboard\'a yönlendiriliyorsunuz...');
        setTimeout(() => navigate('/dashboard'), 1000);
      } else if (success?.status === 'redirecting_to_payment') {
        // Paid plan - redirecting to Stripe (window.location.href already set)
        toast.success('Kayıt başarılı! Ödeme sayfasına yönlendiriliyorsunuz...');
        // No need to navigate, already redirecting
      } else if (success === false) {
        // Registration failed - check for email error
        // Do nothing, error will be handled in catch block
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Kayıt sırasında bir hata oluştu';
      
      // Check if it's a duplicate email error
      if (errorMessage.includes('e-posta') || errorMessage.includes('email')) {
        setEmailError('Bu e-posta adresi ile sistemimize kayıtlı bir kullanıcı bulunmaktadır. Lütfen farklı bir e-posta adresi deneyiniz.');
      } else {
        toast.error(errorMessage);
      }
      
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Active campaign banner — Ultra Admin > Kampanyalar.
              Urgency tiers same as Subscription page: safe/urgent/critical/final.
              Only shown if the campaign actually applies to at least one plan. */}
          {activeCampaign && activeCampaign.discount_percentage > 0
            && Array.isArray(activeCampaign.applicable_packages)
            && activeCampaign.applicable_packages.length > 0
            && (() => {
            const secs = Number(activeCampaign.seconds_remaining ?? 0);
            const days = Number(activeCampaign.days_remaining ?? 0);
            let tier = 'safe';
            let timeLabel;
            if (secs > 0) {
              if (secs < 3600) {
                tier = 'final';
                timeLabel = `Son ${Math.max(1, Math.floor(secs / 60))} dakika!`;
              } else if (secs < 86400) {
                tier = 'critical';
                timeLabel = `Son ${Math.max(1, Math.floor(secs / 3600))} saat!`;
              } else if (days <= 3) {
                tier = 'urgent';
                timeLabel = `${days} gün kaldı`;
              } else {
                tier = 'safe';
                timeLabel = `${days} gün kaldı`;
              }
            }
            const styles = {
              safe:     { box: 'border-orange-400 from-orange-50 via-amber-50 to-orange-50', chip: 'bg-orange-500', pulse: '' },
              urgent:   { box: 'border-red-400 from-red-50 via-orange-50 to-red-50', chip: 'bg-red-500', pulse: '' },
              critical: { box: 'border-red-500 from-red-100 via-red-50 to-red-100', chip: 'bg-red-600', pulse: 'animate-pulse' },
              final:    { box: 'border-red-600 from-red-100 via-red-50 to-red-100 shadow-md', chip: 'bg-red-700', pulse: 'animate-pulse ring-2 ring-red-400 ring-offset-2' },
            }[tier];
            return (
              <div
                className={`mb-6 flex items-center justify-between gap-4 rounded-xl border-2 bg-gradient-to-r px-4 py-3 ${styles.box} ${styles.pulse}`}
                data-testid="campaign-banner"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white text-lg ${styles.chip}`}>
                    🎉
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900" data-testid="campaign-banner-name">
                      {activeCampaign.name} · %{activeCampaign.discount_percentage} indirim
                    </div>
                    <div className="text-xs text-gray-700">
                      {activeCampaign.applicable_packages && activeCampaign.applicable_packages.length > 0
                        ? `${activeCampaign.applicable_packages.length} planda geçerli`
                        : 'Hiçbir planda geçerli değil'}
                      {' · '}
                      Kayıt sırasında otomatik uygulanır
                    </div>
                  </div>
                </div>
                {timeLabel && (
                  <div className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-bold text-white ${styles.chip}`} data-testid="campaign-countdown">
                    {timeLabel}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Page Title */}
          <div className="text-center mb-8">
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Kayıt Ol
          </h2>
          {inviteData ? (
            <div className="mt-2 text-center max-w-2xl mx-auto">
              <p className="text-lg text-green-700 font-medium leading-relaxed">
                Tebrikler! Advancecash.io tarafından <strong>{inviteData.trial_days} günlük ücretsiz deneme sürümüne</strong> davet edildiniz. 
                Hemen kaydınızı yaparak toplam <strong>{inviteData.max_users} kullanıcıya kadar {inviteData.trial_days} gün boyunca</strong> platformu test edebilir ve şirket masraflarınızı kontrol altına alabilirsiniz. 
                <span className="text-green-600 font-semibold"> Kredi kartı gerekmez ve otomatik uzatma yoktur!</span>
              </p>
            </div>
          ) : (
            <p className="mt-2 text-gray-600">
              Advancecash.io'ya hoş geldiniz! Hemen başlayın.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Registration Form */}
          <div className="lg:col-span-2">
            <Card className="p-8 shadow-xl border-0">
              {/* Invite Banner */}
              {inviteData && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">🎉</div>
                    <div>
                      <h3 className="font-bold text-gray-900">Özel Davet!</h3>
                      <p className="text-sm text-gray-700 mt-1">
                        <strong>{inviteData.trial_days} gün</strong> ücretsiz Enterprise deneme süresi 
                        ve <strong>{inviteData.max_users} kullanıcıya kadar</strong> erişim hakkı!
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                        <span>📧 {inviteData.email}</span>
                        <span>📅 {new Date(inviteData.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
                {/* Plan Selection — 4 kutu (Solo → Starter → Professional → Enterprise) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="form-label">Plan Seçimi</Label>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                      Ücretsiz deneme · Kredi kartı gerekmez
                    </span>
                  </div>

                  {/* Aylık / Yıllık faturalama seçici — kullanıcı yıllık seçerse
                      Ultra Admin'in belirlediği yearly_discount_percent değeri
                      Sipariş Özeti panelinde otomatik uygulanır. */}
                  <div className="mb-3 flex items-center justify-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1" data-testid="billing-cycle-toggle">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, subscription_type: 'monthly' })}
                      className={`flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        formData.subscription_type === 'monthly'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="billing-cycle-monthly"
                    >
                      Aylık
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, subscription_type: 'yearly' })}
                      className={`flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
                        formData.subscription_type === 'yearly'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="billing-cycle-yearly"
                    >
                      Yıllık
                      {(() => {
                        // Show the MAX yearly discount across all active plans
                        const maxDiscount = Math.max(
                          0,
                          ...(availablePackages || []).map(p =>
                            Number(p.yearlyDiscountPercent ?? p.yearly_discount_percent ?? 0)
                          )
                        );
                        return maxDiscount > 0 ? (
                          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                            %{maxDiscount} indirim
                          </span>
                        ) : null;
                      })()}
                    </button>
                  </div>

                  {/* Ultra Admin promosyon daveti — plan kilitleme uyarısı */}
                  {promoInfo?.selected_plan && (
                    <div
                      className="mb-3 p-3 rounded-lg border-2 border-emerald-500 bg-emerald-50 text-sm text-emerald-900 flex items-start gap-2"
                      data-testid="promo-plan-lock-banner"
                    >
                      <span className="text-lg leading-none">🔒</span>
                      <div>
                        <div className="font-semibold">
                          Bu davet <span className="capitalize">{promoInfo.selected_plan}</span> planına özeldir
                        </div>
                        <div className="text-xs text-emerald-800/80 mt-0.5">
                          Diğer planlar bu davet için seçilemez. Başka bir plana geçmek isterseniz promosyon kodunu boş bırakın veya normal <a href="/register" className="underline">kayıt sayfasını</a> kullanın.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {/* Plan slugs that are active (Ultra Admin can pasive any of them) */}
                    {(() => {
                      const activeCodes = new Set(
                        (availablePackages || [])
                          .map(p => (p.code || p.id || '').toLowerCase())
                          .filter(Boolean)
                      );
                      // Trial days lookup per slug (Ultra Admin can customise).
                      const trialDaysOf = (slug) => {
                        const key = String(slug || '').toLowerCase();
                        const found = (availablePackages || []).find(p =>
                          String(p.code || p.id || '').toLowerCase() === key
                        );
                        const d = Number(found?.trialDays ?? found?.trial_days);
                        return Number.isFinite(d) && d > 0 ? d : 14;
                      };
                      const showSolo = activeCodes.has('solo');
                      const showStarter = activeCodes.has('starter');
                      const showProfessional = activeCodes.has('professional');
                      const showEnterprise = activeCodes.has('enterprise');
                      const isYearlyCards = formData.subscription_type === 'yearly';
                      // Dynamic price for a given plan slug, respecting both
                      // (1) active campaign discount (only on selected packages)
                      // and (2) yearly discount when the cycle toggle = yearly.
                      const priceFor = (slug) => {
                        const key = String(slug || '').toLowerCase();
                        const pkg = (availablePackages || []).find(p =>
                          String(p.code || p.id || '').toLowerCase() === key
                        );
                        const base = Number(pkg?.price ?? 0);
                        let campaignPct = 0;
                        if (activeCampaign && activeCampaign.discount_percentage > 0) {
                          const list = activeCampaign.applicable_packages || [];
                          if (list.map(s => String(s).toLowerCase()).includes(key)) {
                            campaignPct = Math.max(0, Math.min(90, Number(activeCampaign.discount_percentage) || 0));
                          }
                        }
                        const monthly = +(base * (1 - campaignPct / 100)).toFixed(2);
                        if (!isYearlyCards) {
                          // Monthly view: cross out only when a campaign is
                          // actively lowering this plan's price.
                          return {
                            display: monthly.toFixed(2),
                            suffix: '/ay',
                            hasCampaign: campaignPct > 0,
                            campaignPct,
                            hasDiscount: campaignPct > 0,
                            original: campaignPct > 0 ? base.toFixed(2) : null,
                            base,
                          };
                        }
                        const rawYearly = Number(pkg?.yearlyDiscountPercent ?? pkg?.yearly_discount_percent ?? 20);
                        const yPct = Math.max(0, Math.min(90, isFinite(rawYearly) ? rawYearly : 20));
                        const yearlyFull = +(base * 12).toFixed(2);
                        const yearly = +(monthly * 12 * (1 - yPct / 100)).toFixed(2);
                        // Yearly view: cross out list price (base * 12) whenever
                        // ANY discount is applied — campaign, yearly, or both.
                        const hasDiscount = campaignPct > 0 || yPct > 0;
                        return {
                          display: yearly.toFixed(2),
                          suffix: '/yıl',
                          hasCampaign: campaignPct > 0,
                          campaignPct,
                          hasDiscount,
                          original: hasDiscount ? yearlyFull.toFixed(2) : null,
                          base,
                        };
                      };
                      return (
                        <>
                    {/* Solo Plan (en ucuz, sol-üst) */}
                    {showSolo && (
                    <div 
                      className={`p-3 border-2 rounded-lg transition-all relative ${
                        promoInfo?.selected_plan && promoInfo.selected_plan !== 'solo'
                          ? 'opacity-40 grayscale cursor-not-allowed border-gray-200'
                          : 'cursor-pointer'
                      } ${
                        formData.selected_plan === 'solo' 
                          ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (promoInfo?.selected_plan && promoInfo.selected_plan !== 'solo') return;
                        setFormData({...formData, selected_plan: 'solo'});
                      }}
                      data-testid="register-plan-solo"
                    >
                      <div className="absolute -top-2 right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                        {trialDaysOf('solo')} gün ücretsiz
                      </div>
                      <div className="text-center">
                        <span className="text-emerald-600 text-2xl">🧑‍💻</span>
                        <h4 className="font-medium text-gray-900 mt-1">{planNameFromPackages('solo', availablePackages, lang, 'Solo')}</h4>
                        <p className="text-xs text-gray-600 mt-1">Tek kişi · Onay akışı yok</p>
                        <div className="mt-2">
                          <span className="text-lg font-bold text-emerald-600">
                            {symbol('solo')}{priceFor('solo').display}
                          </span>
                          <span className="text-xs text-gray-500">{priceFor('solo').suffix}</span>
                        </div>
                        {priceFor('solo').original && (
                          <div className="text-[11px] text-gray-400 line-through mt-0.5" data-testid="register-plan-solo-original-price">
                            {symbol('solo')}{priceFor('solo').original}{priceFor('solo').suffix}
                          </div>
                        )}
                        {priceFor('solo').hasCampaign && (
                          <div className="campaign-shimmer-badge mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="register-plan-solo-campaign-badge">
                            <span className="relative z-10">🎉 Kampanya %{priceFor('solo').campaignPct}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1">{trialDaysOf('solo')} gün sonra</p>
                      </div>
                    </div>
                    )}

                    {/* Starter Plan */}
                    {showStarter && (
                    <div 
                      className={`p-3 border-2 rounded-lg transition-all relative ${
                        promoInfo?.selected_plan && promoInfo.selected_plan !== 'starter'
                          ? 'opacity-40 grayscale cursor-not-allowed border-gray-200'
                          : 'cursor-pointer'
                      } ${
                        formData.selected_plan === 'starter' 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (promoInfo?.selected_plan && promoInfo.selected_plan !== 'starter') return;
                        setFormData({...formData, selected_plan: 'starter'});
                      }}
                      data-testid="register-plan-starter"
                    >
                      <div className="absolute -top-2 right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                        {trialDaysOf('starter')} gün ücretsiz
                      </div>
                      <div className="text-center">
                        <span className="text-blue-600 text-2xl">🚀</span>
                        <h4 className="font-medium text-gray-900 mt-1">{planNameFromPackages('starter', availablePackages, lang, 'Starter')}</h4>
                        <p className="text-xs text-gray-600 mt-1">5 kullanıcıya kadar</p>
                        <div className="mt-2">
                          <span className="text-lg font-bold text-blue-600">
                            {symbol('starter')}{priceFor('starter').display}
                          </span>
                          <span className="text-xs text-gray-500">{priceFor('starter').suffix}</span>
                        </div>
                        {priceFor('starter').original && (
                          <div className="text-[11px] text-gray-400 line-through mt-0.5" data-testid="register-plan-starter-original-price">
                            {symbol('starter')}{priceFor('starter').original}{priceFor('starter').suffix}
                          </div>
                        )}
                        {priceFor('starter').hasCampaign && (
                          <div className="campaign-shimmer-badge mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="register-plan-starter-campaign-badge">
                            <span className="relative z-10">🎉 Kampanya %{priceFor('starter').campaignPct}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1">{trialDaysOf('starter')} gün sonra</p>
                      </div>
                    </div>
                    )}

                    {/* Professional Plan */}
                    {showProfessional && (
                    <div 
                      className={`p-3 border-2 rounded-lg transition-all relative ${
                        promoInfo?.selected_plan && promoInfo.selected_plan !== 'professional'
                          ? 'opacity-40 grayscale cursor-not-allowed border-gray-200'
                          : 'cursor-pointer'
                      } ${
                        formData.selected_plan === 'professional' 
                          ? 'border-purple-500 bg-purple-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (promoInfo?.selected_plan && promoInfo.selected_plan !== 'professional') return;
                        setFormData({...formData, selected_plan: 'professional'});
                      }}
                      data-testid="register-plan-professional"
                    >
                      <div className="absolute -top-2 right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                        {trialDaysOf('professional')} gün ücretsiz
                      </div>
                      <div className="absolute -top-2 left-2 bg-purple-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                        En Popüler
                      </div>
                      <div className="text-center">
                        <span className="text-purple-600 text-2xl">👑</span>
                        <h4 className="font-medium text-gray-900 mt-1">{planNameFromPackages('professional', availablePackages, lang, 'Professional')}</h4>
                        <p className="text-xs text-gray-600 mt-1">15 kullanıcıya kadar</p>
                        <div className="mt-2">
                          <span className="text-lg font-bold text-purple-600">
                            {symbol('professional')}{priceFor('professional').display}
                          </span>
                          <span className="text-xs text-gray-500">{priceFor('professional').suffix}</span>
                        </div>
                        {priceFor('professional').original && (
                          <div className="text-[11px] text-gray-400 line-through mt-0.5" data-testid="register-plan-professional-original-price">
                            {symbol('professional')}{priceFor('professional').original}{priceFor('professional').suffix}
                          </div>
                        )}
                        {priceFor('professional').hasCampaign && (
                          <div className="campaign-shimmer-badge mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="register-plan-professional-campaign-badge">
                            <span className="relative z-10">🎉 Kampanya %{priceFor('professional').campaignPct}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1">{trialDaysOf('professional')} gün sonra</p>
                      </div>
                    </div>
                    )}

                    {/* Enterprise Plan */}
                    {showEnterprise && (
                    <div 
                      className={`p-3 border-2 rounded-lg transition-all relative ${
                        promoInfo?.selected_plan && promoInfo.selected_plan !== 'enterprise'
                          ? 'opacity-40 grayscale cursor-not-allowed border-gray-200'
                          : 'cursor-pointer'
                      } ${
                        formData.selected_plan === 'enterprise' 
                          ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (promoInfo?.selected_plan && promoInfo.selected_plan !== 'enterprise') return;
                        setFormData({...formData, selected_plan: 'enterprise'});
                      }}
                      data-testid="register-plan-enterprise"
                    >
                      <div className="absolute -top-2 right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                        {trialDaysOf('enterprise')} gün ücretsiz
                      </div>
                      <div className="text-center">
                        <span className="text-indigo-600 text-2xl">🏢</span>
                        <h4 className="font-medium text-gray-900 mt-1">{planNameFromPackages('enterprise', availablePackages, lang, 'Enterprise')}</h4>
                        <p className="text-xs text-gray-600 mt-1">50 kullanıcıya kadar</p>
                        <div className="mt-2">
                          <span className="text-lg font-bold text-indigo-600">
                            {symbol('enterprise')}{priceFor('enterprise').display}
                          </span>
                          <span className="text-xs text-gray-500">{priceFor('enterprise').suffix}</span>
                        </div>
                        {priceFor('enterprise').original && (
                          <div className="text-[11px] text-gray-400 line-through mt-0.5" data-testid="register-plan-enterprise-original-price">
                            {symbol('enterprise')}{priceFor('enterprise').original}{priceFor('enterprise').suffix}
                          </div>
                        )}
                        {priceFor('enterprise').hasCampaign && (
                          <div className="campaign-shimmer-badge mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="register-plan-enterprise-campaign-badge">
                            <span className="relative z-10">🎉 Kampanya %{priceFor('enterprise').campaignPct}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1">{trialDaysOf('enterprise')} gün sonra</p>
                      </div>
                    </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Kayıt sırasında kredi kartı gerekmez. Devam etmek istemeniz durumunda seçtiğiniz planın
                    ücretsiz deneme süresi bittiğinde aylık aboneliği başlatabilirsiniz. İstediğiniz zaman
                    iptal edebilir ya da farklı bir plana geçebilirsiniz.
                  </p>
                </div>

                {/* Ödeme türü (aylık/yıllık) 14 gün trial sonrası /subscription sayfasında seçilir. */}

                {/* Email and Password */}
                <div>
                  <Label htmlFor="contact_email" className="form-label">
                    E-posta Adresi *
                  </Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    className={`form-input ${promoInfo?.invite_email ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="admin@abcteknoloji.com"
                    required
                    readOnly={!!promoInfo?.invite_email}
                    data-testid="email-input"
                  />
                  {promoInfo?.invite_email ? (
                    <p className="text-emerald-700 text-xs mt-1 flex items-center gap-1">
                      🔒 Bu davet <strong>{promoInfo.invite_email}</strong> adresine düzenlenmiştir — değiştirilemez.
                    </p>
                  ) : emailError && (
                    <p className="text-red-600 text-sm mt-1">{emailError}</p>
                  )}
                </div>

                {/* Account Type Selection */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_company"
                    name="is_company"
                    checked={formData.is_company}
                    onCheckedChange={(checked) => handleSelectChange('is_company', checked)}
                    data-testid="company-checkbox"
                  />
                  <Label htmlFor="is_company" className="text-sm">
                    Şirket adına kayıt yap (Fatura bilgileri gerekir)
                  </Label>
                </div>

                {/* Company Details (shown only if company registration) */}
                {formData.is_company && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label htmlFor="company_name" className="form-label">
                        Şirket Adı *
                      </Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="ABC Teknoloji A.Ş."
                        required={formData.is_company}
                        data-testid="company-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tax_number" className="form-label">
                        Vergi Numarası (Opsiyonel)
                      </Label>
                      <Input
                        id="tax_number"
                        name="tax_number"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        value={formData.tax_number}
                        onChange={(e) => {
                          // Yalnızca rakam; maks 10 basamak (TR vergi no).
                          const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleChange({ target: { name: 'tax_number', value: digitsOnly } });
                        }}
                        className="form-input"
                        placeholder="10 basamaklı vergi numarası"
                        data-testid="tax-number-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(formData.tax_number || '').length}/10 basamak — sadece rakam
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="address" className="form-label">
                        Adres *
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Şirket adresi"
                        required={formData.is_company}
                        data-testid="address-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="country" className="form-label">
                          Ülke *
                        </Label>
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="form-input"
                          required={formData.is_company}
                          data-testid="country-select"
                        >
                          <option value="">Ülke seçiniz</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label htmlFor="city" className="form-label">
                          Şehir *
                        </Label>
                        {cities.length > 0 ? (
                          <select
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            required={formData.is_company}
                            disabled={!formData.country || loadingCities}
                            data-testid="city-select"
                          >
                            <option value="">
                              {loadingCities ? 'Yükleniyor...' : 'Şehir seçiniz'}
                            </option>
                            {cities.map((city) => (
                              <option key={city.id} value={city.name}>
                                {city.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Şehir girin (örn: New York, Berlin)"
                            required={formData.is_company}
                            disabled={!formData.country || loadingCities}
                            data-testid="city-input"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Password */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password" className="form-label">
                      Şifre *
                    </Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handlePasswordBlur}
                      className={`form-input ${passwordError ? 'border-red-500' : ''}`}
                      placeholder="••••••••"
                      required
                      data-testid="password-input"
                    />
                    <PasswordError error={passwordError} />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password" className="form-label">
                      Şifre Tekrar *
                    </Label>
                    <PasswordInput
                      id="confirm_password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      onBlur={handleConfirmPasswordBlur}
                      className={`form-input ${confirmPasswordError ? 'border-red-500' : ''}`}
                      placeholder="••••••••"
                      required
                      data-testid="confirm-password-input"
                    />
                    <PasswordError error={confirmPasswordError} />
                  </div>
                </div>

                {/* Promosyon Kodu — Ultra Admin daveti (opsiyonel) */}
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="promo-block">
                  <Label htmlFor="promo_code" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <span>🎁</span> Promosyon Kodu <span className="text-gray-400 font-normal">(opsiyonel)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="promo_code"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase().slice(0, 12));
                        if (promoInfo) setPromoInfo(null);
                        if (promoError) setPromoError('');
                      }}
                      placeholder="Ör: ABC12XYZ4"
                      className="form-input font-mono tracking-wider uppercase"
                      data-testid="promo-code-input"
                    />
                    <button
                      type="button"
                      disabled={!promoCode || promoChecking}
                      onClick={async () => {
                        if (!promoCode) return;
                        setPromoChecking(true);
                        setPromoError('');
                        try {
                          const res = await axios.post(`${API}/promo-codes/validate`, {
                            code: promoCode,
                            email: formData.contact_email || null,
                          });
                          setPromoInfo(res.data);
                          toast.success('🎉 Promosyon kodu geçerli!');
                        } catch (err) {
                          setPromoInfo(null);
                          setPromoError(err.response?.data?.detail || 'Kod doğrulanamadı');
                        } finally {
                          setPromoChecking(false);
                        }
                      }}
                      className="px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="promo-validate-btn"
                    >
                      {promoChecking ? '...' : 'Doğrula'}
                    </button>
                  </div>
                  {promoError && (
                    <div className="text-xs text-rose-600 mt-1">{promoError}</div>
                  )}
                  {promoInfo && (
                    <div className="text-xs text-emerald-700 bg-emerald-100 rounded p-2 space-y-0.5" data-testid="promo-info-box">
                      <div>Plan: <strong className="capitalize">{promoInfo.selected_plan}</strong></div>
                      {promoInfo.free_days > 0 && (
                        <div>🎁 <strong>{promoInfo.free_days} gün</strong> ücretsiz</div>
                      )}
                      {promoInfo.discount_percent > 0 && (
                        <div>💸 <strong>%{promoInfo.discount_percent}</strong> indirim / {promoInfo.discount_duration_value} {({day:'gün',month:'ay',year:'yıl'})[promoInfo.discount_duration_unit] || ''}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Terms Agreement — modal ile okuma zorunlu */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="agreed_terms"
                      name="agreed_terms"
                      checked={formData.agreed_terms}
                      onChange={(e) => {
                        if (!legalReady) {
                          e.preventDefault();
                          return;
                        }
                        handleChange(e);
                      }}
                      disabled={!legalReady}
                      required
                      data-testid="terms-checkbox"
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                    <label htmlFor="agreed_terms" className={`text-sm ${legalReady ? 'cursor-pointer' : 'cursor-not-allowed text-gray-500'}`}>
                      <button
                        type="button"
                        onClick={() => setLegalModal({ show: true, type: 'terms' })}
                        className="text-teal-600 hover:underline font-medium inline-flex items-center gap-1"
                        data-testid="open-terms-link"
                      >
                        Kullanım Koşulları
                        {termsRead && <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />}
                      </button>{' '}
                      ve{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModal({ show: true, type: 'privacy' })}
                        className="text-teal-600 hover:underline font-medium inline-flex items-center gap-1"
                        data-testid="open-privacy-link"
                      >
                        Gizlilik Politikası
                        {privacyRead && <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />}
                      </button>
                      &apos;nı kabul ediyorum
                    </label>
                  </div>
                  {!legalReady && (
                    <div className="ml-6 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>
                        Kutucuğu işaretleyebilmek için önce her iki metni de <strong>açıp sonuna kadar okumanız</strong> ve <strong>&quot;Okudum, Anladım&quot;</strong> butonuna basmanız gerekir.
                      </span>
                    </div>
                  )}
                  {legalReady && !formData.agreed_terms && (
                    <div className="ml-6 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 flex items-center gap-1">
                      <Check className="w-3 h-3" strokeWidth={3} />
                      <span>Her iki metin de okundu — artık kutucuğu işaretleyebilirsiniz.</span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.agreed_terms}
                  className="btn-primary w-full flex justify-center py-3"
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <span>
                      Ücretsiz Başla · 14 Gün
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Zaten hesabınız var mı?{' '}
                  <Link to="/login" className="font-medium text-teal-600 hover:text-teal-500" data-testid="login-link">
                    Giriş Yapın
                  </Link>
                </p>
              </div>
            </Card>
          </div>

          {/* Pricing Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 shadow-xl border-0 sticky top-8">
              {/* Compute dynamic trial-days & yearly discount for the selected plan */}
              {(() => { return null; })()}
              {(() => {
                // No-op — inline consts below via IIFE.
                return null;
              })()}
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Sipariş Özeti
              </h3>

              {(() => {
                const currentPkg = (availablePackages || []).find(p =>
                  String(p.code || p.id || '').toLowerCase() === String(formData.selected_plan || '').toLowerCase()
                );
                const trialDays = Number(currentPkg?.trialDays ?? currentPkg?.trial_days) || 14;
                const rawDiscount = Number(currentPkg?.yearlyDiscountPercent ?? currentPkg?.yearly_discount_percent ?? 20);
                const discountPct = Math.max(0, Math.min(90, isFinite(rawDiscount) ? rawDiscount : 20));

                // Layer 2 — Campaign discount (Ultra Admin > Kampanyalar).
                // Only applies to plans explicitly listed in the campaign's
                // `applicable_packages`. Empty list = campaign is dormant.
                let campaignPct = 0;
                if (activeCampaign && activeCampaign.discount_percentage > 0) {
                  const list = activeCampaign.applicable_packages || [];
                  const applies = list.length > 0
                    && list.map(s => String(s).toLowerCase())
                        .includes(String(formData.selected_plan || '').toLowerCase());
                  if (applies) campaignPct = Math.max(0, Math.min(90, Number(activeCampaign.discount_percentage) || 0));
                }
                const baseMonthly = Number(currentPkg?.price ?? selectedPlan.price ?? 0);
                const monthlyPrice = +(baseMonthly * (1 - campaignPct / 100)).toFixed(2);
                const yearlyFull = +(monthlyPrice * 12).toFixed(2);
                const yearlyDiscounted = +(yearlyFull * (1 - discountPct / 100)).toFixed(2);
                const savings = +(yearlyFull - yearlyDiscounted).toFixed(2);
                const isYearly = formData.subscription_type === 'yearly';
                const afterTrialLabel = isYearly
                  ? (lang === 'tr' ? '/yıl' : '/yr')
                  : (lang === 'tr' ? '/ay' : '/mo');
                const afterTrialPrice = isYearly ? yearlyDiscounted : monthlyPrice;

                return (
              <div className="space-y-5">
                {/* ⚡ En üstte "kart gerekmez" vurgusu — profesyonel güven bandı */}
                <div className="relative overflow-hidden rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm">
                  <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-emerald-500/10" />
                  <div className="relative flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white">
                      <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="text-sm font-bold uppercase tracking-wide text-emerald-800">
                        {lang === 'tr' ? 'KREDİ KARTI GEREKMEZ' : 'NO CREDIT CARD REQUIRED'}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                        {lang === 'tr'
                          ? `${trialDays} gün boyunca tüm özellikler ücretsiz. Otomatik ücretlendirme yok.`
                          : `All features free for ${trialDays} days. No auto-billing.`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Seçili Plan kartı */}
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {lang === 'tr' ? 'Seçili Plan' : 'Selected Plan'}
                    </span>
                    <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-teal-700">
                      {lang === 'tr' ? `${trialDays} Gün Ücretsiz` : `${trialDays} Days Free`}
                    </span>
                  </div>
                  <div className="mb-3 text-xl font-bold text-gray-900">
                    {planNameFromPackages(formData.selected_plan, availablePackages, lang, selectedPlan.name)}
                  </div>
                  {(() => {
                    const desc = planDescription(formData.selected_plan, lang, selectedPlan.description);
                    return desc ? (
                      <p className="mb-3 text-xs leading-relaxed text-gray-500">{desc}</p>
                    ) : null;
                  })()}
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="text-gray-400">👥</span>
                    <span>
                      {(typeof selectedPlan.user_limit === 'number' || typeof selectedPlan.user_limit === 'string')
                        ? selectedPlan.user_limit
                        : '—'}{' '}
                      {lang === 'tr' ? 'kullanıcı' : 'user(s)'}
                    </span>
                  </div>
                </div>

                {/* Fiyat blokları */}
                <div className="space-y-3">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-5 text-white shadow-lg">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-emerald-50">
                      <Clock className="h-3.5 w-3.5" />
                      {lang === 'tr' ? `İlk ${trialDays} Gün` : `First ${trialDays} Days`}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-4xl font-black tracking-tight">
                        {symbol(formData.selected_plan)}0
                      </span>
                      <span className="text-sm font-medium text-emerald-100">
                        {lang === 'tr' ? `/ ${trialDays} gün` : `/ ${trialDays} days`}
                      </span>
                    </div>
                    <div className="mt-2 text-xs font-medium text-emerald-50">
                      {lang === 'tr' ? '✓ Ücretsiz · ✓ Kartsız · ✓ Tam özellik' : '✓ Free · ✓ No card · ✓ Full features'}
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                      <span>{lang === 'tr' ? `${trialDays + 1}. günden sonra` : `After day ${trialDays + 1}`}</span>
                    </div>
                    <div className="text-sm font-bold text-gray-900" data-testid="after-trial-price">
                      {symbol(formData.selected_plan)}{afterTrialPrice.toFixed(2)}
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        {afterTrialLabel}
                      </span>
                    </div>
                  </div>

                  {/* Yıllık indirim çağrısı — Ultra Admin'den gelen yüzde ile
                      dinamik hesaplanan tasarruf gösterilir. Sadece kullanıcı
                      AYLIK seçtiğinde gösterilir (yıllıktaysa fiyat zaten
                      indirimli). */}
                  {!isYearly && discountPct > 0 && monthlyPrice > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, subscription_type: 'yearly' })}
                      className="w-full flex items-center justify-between rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 px-4 py-3 text-left transition-colors"
                      data-testid="yearly-discount-cta"
                    >
                      <div className="flex items-center gap-2 text-xs text-purple-800">
                        <Sparkles className="h-3.5 w-3.5 text-purple-600" />
                        <span className="font-semibold">
                          {lang === 'tr'
                            ? `Yıllığa geç, %${discountPct} indirim kazan`
                            : `Switch to yearly and save ${discountPct}%`}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 line-through">
                          {symbol(formData.selected_plan)}{yearlyFull.toFixed(2)}
                        </div>
                        <div className="text-sm font-bold text-purple-700">
                          {symbol(formData.selected_plan)}{yearlyDiscounted.toFixed(2)}
                          <span className="ml-1 text-[10px] font-normal text-purple-600">
                            {lang === 'tr' ? '/yıl' : '/yr'}
                          </span>
                        </div>
                        <div className="text-[10px] font-medium text-emerald-600">
                          {lang === 'tr' ? `${symbol(formData.selected_plan)}${savings.toFixed(2)} tasarruf` : `${symbol(formData.selected_plan)}${savings.toFixed(2)} saved`}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Yıllık seçildiğinde onay banner'ı */}
                  {isYearly && discountPct > 0 && monthlyPrice > 0 && (
                    <div
                      className="flex items-center justify-between rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3"
                      data-testid="yearly-savings-badge"
                    >
                      <div className="flex items-center gap-2 text-xs text-emerald-800">
                        <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={3} />
                        <span className="font-semibold">
                          {lang === 'tr'
                            ? `Yıllık ödeme · %${discountPct} indirim uygulandı`
                            : `Yearly billing · ${discountPct}% off applied`}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 line-through">
                          {symbol(formData.selected_plan)}{yearlyFull.toFixed(2)}
                        </div>
                        <div className="text-[10px] font-medium text-emerald-700">
                          {lang === 'tr'
                            ? `${symbol(formData.selected_plan)}${savings.toFixed(2)} tasarruf`
                            : `${symbol(formData.selected_plan)}${savings.toFixed(2)} saved`}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dinamik özellik listesi — Ultra Admin > Özellik Yönetimi'nden gelir */}
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-teal-600" />
                    <h4 className="text-sm font-bold text-gray-900">
                      {lang === 'tr' ? 'Pakete Dahil Özellikler' : 'Included Features'}
                    </h4>
                    <span className="ml-auto rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700">
                      {planFeatureLabels.length}
                    </span>
                  </div>
                  {planFeatureLabels.length > 0 ? (
                    <TooltipProvider delayDuration={150}>
                      <ul className="grid grid-cols-1 gap-1.5">
                        {planFeatureLabels.map((f) => (
                          <li
                            key={f.code}
                            data-testid={`plan-feature-${f.code}`}
                            className="flex items-start gap-2 text-xs text-gray-700"
                          >
                            <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" strokeWidth={3} />
                            {f.desc ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="cursor-help border-b border-dotted border-gray-300 hover:border-teal-500 hover:text-teal-700 inline-flex items-center gap-1 transition-colors"
                                    data-testid={`plan-feature-tooltip-trigger-${f.code}`}
                                  >
                                    {f.label}
                                    <Info className="h-3 w-3 text-gray-300" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="left"
                                  align="center"
                                  className="max-w-[260px] bg-gray-900 text-gray-100 text-[11px] leading-relaxed px-3 py-2 shadow-xl"
                                >
                                  <div className="font-semibold text-white mb-1">{f.label}</div>
                                  <div className="text-gray-200">{f.desc}</div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span>{f.label}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </TooltipProvider>
                  ) : (
                    <p className="text-xs italic text-gray-400">
                      {lang === 'tr' ? 'Bu plan için özellik tanımlı değil.' : 'No features defined for this plan.'}
                    </p>
                  )}
                </div>

                <hr className="border-gray-200" />

                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-semibold text-gray-900">
                    {lang === 'tr' ? 'Bugün Ödeyeceğiniz' : 'Due Today'}
                  </span>
                  <div className="text-right">
                    <span className="gradient-text text-2xl font-black">
                      {symbol(formData.selected_plan)}0
                    </span>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      {lang === 'tr' ? `${trialDays} gün ücretsiz` : `${trialDays} days free`}
                    </div>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* Alt güven satırları — profesyonel bilgi bandı */}
              <div className="mt-6 space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <CreditCard className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                  <span className="font-semibold">
                    {lang === 'tr' ? 'Kredi kartı gerekmez' : 'No credit card required'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <Clock className="h-3.5 w-3.5 flex-shrink-0 text-teal-600" />
                  <span>
                    {lang === 'tr'
                      ? 'Tam özellikli ücretsiz deneme'
                      : 'Full-featured free trial'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <XCircle className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                  <span>
                    {lang === 'tr' ? 'İstediğiniz zaman iptal edin' : 'Cancel anytime'}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
    <LegalDocumentModal
      open={legalModal.show && legalModal.type === 'terms'}
      onClose={() => setLegalModal({ show: false, type: null })}
      onAccept={() => setTermsRead(true)}
      title={TERMS_TITLE}
      markdownContent={TERMS_CONTENT}
      alreadyAccepted={termsRead}
    />
    <LegalDocumentModal
      open={legalModal.show && legalModal.type === 'privacy'}
      onClose={() => setLegalModal({ show: false, type: null })}
      onAccept={() => setPrivacyRead(true)}
      title={PRIVACY_TITLE}
      markdownContent={PRIVACY_CONTENT}
      alreadyAccepted={privacyRead}
    />
    </>
  );
};

export default RegisterPage;